import { useRouter } from 'expo-router';
import { Hand, List, Zap } from 'lucide-react-native';
import { useRef, useState } from 'react';
import { View } from 'react-native';

import { OptionRow, PrimaryCTA } from '@/components/onboarding/Controls';
import { Eyebrow, H1, H1Em, Screen, WhyLine } from '@/components/onboarding/Screen';
import { Palette, Spacing } from '@/constants/theme';
import type { SetupMode } from '@/db/types';
import { useOnboarding } from '@/store/onboarding';

export default function Path() {
  const router = useRouter();
  const setMode = useOnboarding((s) => s.setMode);
  const currentMode = useOnboarding((s) => s.mode);
  // Default selection: Quick (recommended) so the Continue button is
  // immediately actionable without a pick. The user can still tap any
  // option to switch — both behaviors converge through `next()`.
  const [picked, setPicked] = useState<SetupMode>(currentMode ?? 'quick');
  // Guards the tap-to-advance path against double navigation (fast
  // double-tap, or tapping a second option during the brief delay).
  const advancing = useRef(false);

  const go = (m: SetupMode) => {
    if (advancing.current) return;
    advancing.current = true;
    setMode(m);
    // Blank skips the entire chip walkthrough — straight to the
    // generator screen which will commit a minimal property using the
    // welcome data only. The user lands on My Home with no systems
    // pre-checked and can add them manually.
    const dest = m === 'blank' ? '/(onboarding)/generating' : '/(onboarding)/home-type';
    router.push(dest as never);
    // Re-arm in case the user comes BACK to this screen (expo-router
    // keeps the component mounted in the stack).
    setTimeout(() => { advancing.current = false; }, 800);
  };

  // Tapping an option both selects it and advances — one tap instead of
  // tap + Continue (Wave 47 onboarding speed-up). The brief delay lets
  // the selection state paint so the tap feels acknowledged.
  const pick = (m: SetupMode) => {
    setPicked(m);
    setMode(m);
    setTimeout(() => go(m), 220);
  };

  const next = () => go(picked);

  return (
    <Screen
      footer={<PrimaryCTA label="Continue" onPress={next} />}>
      <Eyebrow>Setup style</Eyebrow>
      <H1>
        How do you want{'\n'}to <H1Em>set up?</H1Em>
      </H1>
      <WhyLine>
        All three build the same plan. You can switch, pause, or finish
        later — nothing is ever lost.
      </WhyLine>

      <View style={{ marginTop: Spacing.five, gap: Spacing.two }}>
        <OptionRow
          icon={<Zap size={20} color={picked === 'quick' ? Palette.cream : Palette.forest} strokeWidth={1.7} />}
          title="Quick Start"
          subtitle="A handful of questions. We smart-fill the rest — refine anytime. ~2 min"
          badge="Recommended"
          selected={picked === 'quick'}
          onPress={() => pick('quick')}
        />
        <OptionRow
          icon={<List size={20} color={picked === 'full' ? Palette.cream : Palette.forest} strokeWidth={1.7} />}
          title="Full Setup"
          subtitle="Walk every system, add models, filter sizes & photos. ~15 min, pick up & put down anytime"
          selected={picked === 'full'}
          onPress={() => pick('full')}
        />
        {/* Blank Start — for power users who'd rather drive the whole
            setup themselves from My Home + Add a system. We still
            commit the property (so they get a Home tab, a map, etc.)
            but skip every chip question. */}
        <OptionRow
          icon={<Hand size={20} color={picked === 'blank' ? Palette.cream : Palette.forest} strokeWidth={1.7} />}
          title="Blank Start"
          subtitle="Skip the questions. Start from an empty home and add systems your way."
          selected={picked === 'blank'}
          onPress={() => pick('blank')}
        />
      </View>
    </Screen>
  );
}
